import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AppStoreButtons from "@/components/AppStoreButtons";
import { useI18n } from "@/context/I18nContext";
import { InstagramLogo, FacebookLogo, LinkedinLogo, PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const SOCIALS = [
  { name: "Instagram", url: "https://www.instagram.com/teamfit2026",                      Icon: InstagramLogo, testid: "social-instagram" },
  { name: "Facebook",  url: "https://www.facebook.com/share/1JFokiT1sd/?mibextid=wwXIfr", Icon: FacebookLogo,  testid: "social-facebook" },
  { name: "LinkedIn",  url: "https://www.linkedin.com/company/teamfit2026/",              Icon: LinkedinLogo,  testid: "social-linkedin" },
];

/**
 * Global site footer. Visible on every page so users always have:
 *   - App Store + Google Play download buttons (mobile-app conversion)
 *   - All legal/help links (Privacy, Terms, Cookies, FAQ, Support, Contact)
 *   - Brand line + copyright
 *
 * Skipped on the dedicated legal pages — they have their own footer in
 * LegalLayout so we don't double up.
 */
const SKIP_PATHS = ["/privacy", "/terms", "/cookies", "/faq", "/support", "/contact"];

export default function SiteFooter() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  if (SKIP_PATHS.includes(pathname)) return null;

  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t mt-12"
      style={{ borderColor: "var(--tf-border)", background: "var(--tf-surface)" }}
    >
      <div className="max-w-7xl mx-auto px-6 py-10 grid gap-10 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center">
              <span className="font-display text-sm text-white">TF</span>
            </div>
            <span className="font-display text-lg tracking-tight" style={{ color: "var(--tf-text)" }}>
              TEAMFIT
            </span>
          </div>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: "var(--tf-text-2)" }}>
            {lang === "da"
              ? "Find dine træningspartnere til cykling, løb og padel. Lev appen — overalt hvor du er."
              : "Find your training partners for cycling, running and padel. Live the app — wherever you are."}
          </p>

          <div className="mt-6">
            <div className="label-overline mb-3" style={{ color: "var(--tf-text-2)" }}>
              {lang === "da" ? "Hent appen" : "Get the app"}
            </div>
            <AppStoreButtons size="md" />
          </div>

          <div className="mt-8 max-w-sm">
            <div className="label-overline mb-3" style={{ color: "var(--tf-text-2)" }}>
              {lang === "da" ? "Nyhedsbrev" : "Newsletter"}
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--tf-text-2)" }}>
              {lang === "da"
                ? "Nye funktioner, events og træningstips — direkte i din indbakke."
                : "New features, events and training tips — straight to your inbox."}
            </p>
            <NewsletterForm lang={lang} />
          </div>
        </div>

        <div className="md:col-span-4 grid grid-cols-2 gap-x-6 gap-y-2">
          <FooterLink to="/privacy" testId="footer-link-privacy">{t("legal.nav_privacy")}</FooterLink>
          <FooterLink to="/terms" testId="footer-link-terms">{t("legal.nav_terms")}</FooterLink>
          <FooterLink to="/cookies" testId="footer-link-cookies">{t("legal.nav_cookies")}</FooterLink>
          <FooterLink to="/faq" testId="footer-link-faq">{t("legal.nav_faq")}</FooterLink>
          <FooterLink to="/support" testId="footer-link-support">{t("legal.nav_support")}</FooterLink>
          <FooterLink to="/contact" testId="footer-link-contact">{t("legal.nav_contact")}</FooterLink>
        </div>

        <div className="md:col-span-3 md:text-right">
          <div className="label-overline" style={{ color: "var(--tf-text-2)" }}>
            {lang === "da" ? "Kontakt" : "Contact"}
          </div>
          <a
            href="mailto:support@teamfit.dk"
            className="block mt-2 font-display text-base tracking-tight hover:opacity-80"
            style={{ color: "var(--tf-text)" }}
            data-testid="footer-mailto"
          >
            support@teamfit.dk
          </a>
          <div className="mt-1 text-xs" style={{ color: "var(--tf-text-2)" }}>teamfit.dk</div>

          <div className="label-overline mt-6" style={{ color: "var(--tf-text-2)" }}>
            {lang === "da" ? "Følg os" : "Follow us"}
          </div>
          <div className="mt-3 flex gap-3 md:justify-end" data-testid="footer-socials">
            {SOCIALS.map(({ name, url, Icon, testid }) => (
              <a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                data-testid={testid}
                className="w-9 h-9 border flex items-center justify-center hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
                style={{ borderColor: "var(--tf-border)", color: "var(--tf-text-2)" }}
              >
                <Icon weight="fill" size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div
        className="border-t"
        style={{ borderColor: "var(--tf-border)" }}
      >
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div
            className="text-xs"
            style={{ color: "var(--tf-text-2)" }}
            data-testid="footer-copyright"
          >
            © {year} TeamFit. {lang === "da" ? "Alle rettigheder forbeholdes." : "All rights reserved."}
          </div>
          <div className="label-overline" style={{ color: "var(--tf-text-2)" }}>
            {lang === "da" ? "Bevæg. Sammen." : "Move. Together."}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="text-xs uppercase tracking-widest font-semibold py-1 hover:opacity-100 transition-opacity"
      style={{ color: "var(--tf-text-2)" }}
    >
      {children}
    </Link>
  );
}

function NewsletterForm({ lang }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    // very light client-side check — server validates properly
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      toast.error(lang === "da" ? "Ugyldig email" : "Invalid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: value, locale: lang });
      setDone(true);
      setEmail("");
      toast.success(
        lang === "da" ? "Tak — du er tilmeldt!" : "Thanks — you're subscribed!"
      );
    } catch (err) {
      toast.error(
        lang === "da"
          ? "Kunne ikke tilmelde. Prøv igen."
          : "Could not subscribe. Please try again."
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className="text-xs py-2"
        style={{ color: "var(--tf-text)" }}
      >
        {lang === "da"
          ? "✓ Tilmeldt. Vi ses i indbakken."
          : "✓ Subscribed. See you in your inbox."}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      className="flex items-stretch border"
      style={{ borderColor: "var(--tf-border)", background: "var(--tf-bg)" }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={lang === "da" ? "din@email.dk" : "you@email.com"}
        data-testid="newsletter-email-input"
        className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
        style={{ color: "var(--tf-text)" }}
        disabled={busy}
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="newsletter-submit-btn"
        aria-label={lang === "da" ? "Tilmeld" : "Subscribe"}
        className="px-3 flex items-center justify-center text-black hover:opacity-90 transition-opacity disabled:opacity-50"
        style={{ background: "#FFCC00" }}
      >
        <PaperPlaneRight weight="fill" size={16} />
      </button>
    </form>
  );
}
